
  // Owns disclosure and option navigation only. Toggling stays on the server
  // so the selected collection has exactly one source of truth, which is what
  // keeps selections intact across filtering and reopening.
  //
  // Placement and width belong to CSS anchor positioning (the `field-popup`
  // utility). Measuring the trigger here is what used to clamp the popup to
  // the trigger's width and clip long option labels.

  export default {
    mounted() {
      this.trigger = this.el.querySelector("[data-multi-select-trigger]")
      this.popup = this.el.querySelector("[data-multi-select-popup]")
      this.activeIndex = -1
      this.supportsPopover =
        typeof this.popup.showPopover === "function" &&
        typeof this.popup.hidePopover === "function"

      if (!this.supportsPopover) this.popup.hidden = true

      this.onToggle = (event) => {
        const open = event.newState === "open"
        this.trigger.setAttribute("aria-expanded", String(open))
        this.expandedState = open
      }

      if (this.supportsPopover) this.popup.addEventListener("toggle", this.onToggle)

      this.onTriggerKey = (event) => {
        // The trigger only opens. Once the popup is open, onPopupKey —
        // bound on the root, an ancestor of the trigger — owns navigation
        // and activation, which is why an open popup falls through here.
        if (this.expanded()) return
        if (["ArrowDown", "Enter", " "].includes(event.key)) {
          event.preventDefault()
          // Without this the same keydown reaches onPopupKey, which clicks
          // the option just highlighted (Enter, Space) or steps past it
          // (ArrowDown).
          event.stopPropagation()
          this.open()
          this.moveActive(0)
          this.focusFilterOrPopup()
        }
      }
      this.onTriggerClick = (event) => {
        if (!this.supportsPopover) {
          event.preventDefault()
          this.expanded() ? this.close() : this.open()
        }
      }
      this.onPopupKey = (event) => {
        const options = this.options()
        switch (event.key) {
          case "ArrowDown":
            event.preventDefault()
            this.moveActive(options.length ? (this.activeIndex + 1) % options.length : -1)
            break
          case "ArrowUp":
            event.preventDefault()
            this.moveActive(
              options.length ? (this.activeIndex - 1 + options.length) % options.length : -1
            )
            break
          case "Enter":
          case " ":
            if (options[this.activeIndex]) {
              event.preventDefault()
              options[this.activeIndex].click()
            }
            break
          case "Escape":
            event.preventDefault()
            this.close()
            this.trigger.focus()
            break
        }
      }
      this.onDocClick = (event) => {
        if (!this.supportsPopover && !this.el.contains(event.target)) this.close()
      }

      this.trigger.addEventListener("keydown", this.onTriggerKey)
      this.trigger.addEventListener("click", this.onTriggerClick)
      this.el.addEventListener("keydown", this.onPopupKey)
      document.addEventListener("click", this.onDocClick)
    },

    updated() {
      // Re-resolve after a server render and keep the popup open if it was:
      // toggling an option must not collapse the list mid-selection.
      const wasExpanded = this.expandedState
      this.trigger = this.el.querySelector("[data-multi-select-trigger]")
      this.popup = this.el.querySelector("[data-multi-select-popup]")
      if (wasExpanded) this.open()
    },

    destroyed() {
      document.removeEventListener("click", this.onDocClick)
      if (this.supportsPopover) this.popup.removeEventListener("toggle", this.onToggle)
    },

    options() {
      return Array.from(
        this.el.querySelectorAll("[data-multi-select-option]:not([aria-disabled='true'])")
      )
    },
    expanded() {
      return this.supportsPopover
        ? this.popup.matches(":popover-open")
        : !this.popup.hidden
    },
    open() {
      if (this.supportsPopover) {
        if (!this.popup.matches(":popover-open")) this.popup.showPopover()
      } else {
        this.popup.hidden = false
      }
      this.trigger.setAttribute("aria-expanded", "true")
      this.expandedState = true
    },
    close() {
      if (this.supportsPopover) {
        if (this.popup.matches(":popover-open")) this.popup.hidePopover()
      } else {
        this.popup.hidden = true
      }
      this.trigger.setAttribute("aria-expanded", "false")
      this.options().forEach((o) => o.removeAttribute("data-active"))
      this.activeIndex = -1
      this.expandedState = false
    },
    focusFilterOrPopup() {
      const filter = this.el.querySelector("[data-multi-select-filter]")
      if (filter) filter.focus()
    },
    moveActive(index) {
      const options = this.options()
      options.forEach((o) => o.removeAttribute("data-active"))
      this.activeIndex = index
      const active = options[index]
      if (!active) return
      active.setAttribute("data-active", "true")
      active.scrollIntoView({ block: "nearest" })
    },
  }
